//#region IMPORTS
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { closeMobileDash } from "../../features/dashBoard/dashboardSlice";
import { useTranslation } from "react-i18next";
import { setOrderParams } from "../../features/orders/ordersSlice";
import { useLocation } from "react-router-dom";
import DashboardSidebarListItem from "./DashboardSidebarListItem";
import ProductVersionIcon from "../icons/ProductVersionIcon";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import TranslationIcon from "../icons/TranslationIcon";
import useHQmanager from "../../hooks/useHQmanager";
import OverViewIcon from "../icons/OverViewIcon";
import HistoryIcon from "../icons/HistoryIcon";
import ProductIcon from "../icons/ProductIcon";
import UsersIcon from "../icons/UsersIcon";
import HowToIcon from "../icons/HowToIcon";
import PromoIcon from "../icons/PromoIcon";
import ShopIcon from "../icons/ShopIcon";
//#endregion

//#region INTERFACE
interface Props {
  iconwidth?: number;
  dashOpen: boolean;
}
//#endregion

export default function DashboardSidebarList({
  iconwidth = 32,
  dashOpen,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const { pathname, search } = useLocation();
  const splitLocation = pathname.split("/");

  // Redux
  const dispatch = useAppDispatch();
  const { orders_pending, orders_ordered, orders_inProgress } = useAppSelector(
    (state) => state.orders
  );
  const { instoreOrders_pending, instoreOrders_ordered, instoreOrders_inProgress } =
    useAppSelector((state) => state.instoreOrders);
  const { mobileDashOpen } = useAppSelector((state) => state.dashboard);

  // Roles
  const { user } = useAppSelector((state) => state.account);
  const isPicker = usePickerManager(user);
  const isHq = useHQmanager(user);
  const isAdmin = useAdminManager(user);

  // "Orders" sidebar badge = sum of the flow work the role can act on, across
  // promo + instore (kept in sync with the tab badges on /orders).
  //  - HQ     → approve (pending) for both
  //  - admin  → approve + manage + send for both
  //  - picker → instore approve + manage + send for both, but NO promo approve
  //             (pickers don't approve promo orders)
  const ordersBadgeCount = isPicker
    ? orders_ordered +
      orders_inProgress +
      instoreOrders_pending +
      instoreOrders_ordered +
      instoreOrders_inProgress
    : isAdmin
      ? orders_pending +
        orders_ordered +
        orders_inProgress +
        instoreOrders_pending +
        instoreOrders_ordered +
        instoreOrders_inProgress
      : isHq
        ? orders_pending + instoreOrders_pending
        : 0;
  const ordersBadgeTooltip =
    ordersBadgeCount > 0
      ? isHq
        ? `${ordersBadgeCount} order${
            ordersBadgeCount === 1 ? "" : "s"
          } to approve`
        : `${ordersBadgeCount} order${
            ordersBadgeCount === 1 ? "" : "s"
          } to handle`
      : undefined;

  const closeMobileDashHandler = () => {
    mobileDashOpen && dispatch(closeMobileDash());
  };
  //#endregion

  //#region RENDER
  return (
    <nav className="flex flex-1 flex-col ml-2">
      <ul className="flex flex-1 flex-col justify-center gap-y-10">
        {/* HOME */}
        <DashboardSidebarListItem
          to="/"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.home")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === ""}
        >
          <OverViewIcon width={iconwidth} selected={splitLocation[1] === ""} />
        </DashboardSidebarListItem>

        {/* PROMOS */}
        <DashboardSidebarListItem
          to="/promos"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.projects")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "promos"}
        >
          <PromoIcon
            width={iconwidth}
            selected={splitLocation[1] === "promos"}
          />
        </DashboardSidebarListItem>

        {/* STOREMANAGER */}
        <DashboardSidebarListItem
          to="/stores"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.stores")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "stores"}
          shop={false}
          picker={true}
          admin={true}
          headquarters={true}
        >
          <ShopIcon
            width={iconwidth}
            selected={splitLocation[1] === "stores"}
          />
        </DashboardSidebarListItem>

        {/* PRODUCTS */}
        <DashboardSidebarListItem
          to="/products"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.products")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "products"}
          shop={false}
          admin={true}
          picker={false}
          headquarters={false}
        >
          <ProductIcon
            width={iconwidth}
            selected={splitLocation[1] === "products"}
          />
        </DashboardSidebarListItem>

        {/* PRODUCTVERSIONS */}
        <DashboardSidebarListItem
          to="/productversions"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.versions")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "productversions"}
          shop={false}
          admin={true}
          headquarters={false}
          picker={false}
        >
          <ProductVersionIcon
            width={iconwidth}
            selected={splitLocation[1] === "productversions"}
          />
        </DashboardSidebarListItem>

        {/* ALL ORDERS */}
        <DashboardSidebarListItem
          to="/orders"
          linkName={t("rituals.orders")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "orders"}
          countItem={ordersBadgeCount}
          tooltipId={
            isPicker || isAdmin || isHq
              ? "sidebar-orders-workflow-count"
              : undefined
          }
          tooltipContent={ordersBadgeTooltip}
          clickhandler={() => {
            dispatch(
              setOrderParams({
                page: 1,
                limit: 25,
                sort: JSON.stringify({ column: "order_date", order: "desc" }),
                include_sub_orders: 1,
              })
            );
            closeMobileDashHandler();
          }}
        >
          <HistoryIcon
            width={iconwidth}
            selected={splitLocation[1] === "orders"}
          />
        </DashboardSidebarListItem>

        {/* USER MANAGER*/}
        <DashboardSidebarListItem
          to="/users"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.users")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "users"}
          shop={false}
          admin={true}
          headquarters={false}
          picker={false}
        >
          <UsersIcon
            width={iconwidth}
            selected={splitLocation[1] === "users"}
          />
        </DashboardSidebarListItem>

        {/* FEATURELIST */}
        <DashboardSidebarListItem
          to="/featurelist"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.howTo")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "featurelist"}
          admin={false}
          picker={false}
        >
          <HowToIcon
            width={iconwidth}
            selected={splitLocation[1] === "featurelist"}
          />
        </DashboardSidebarListItem>

        {/* ORDER BULK-ACTION RULES (admin reference) */}
        <DashboardSidebarListItem
          to="/order-rules"
          clickhandler={closeMobileDashHandler}
          linkName="Order rules"
          dashOpen={dashOpen}
          selected={splitLocation[1] === "order-rules"}
          headquarters={false}
          picker={false}
          admin={true}
          shop={false}
        >
          <HowToIcon
            width={iconwidth}
            selected={splitLocation[1] === "order-rules"}
          />
        </DashboardSidebarListItem>

        {/* TRANSLATION */}
        <DashboardSidebarListItem
          to="/translations"
          clickhandler={closeMobileDashHandler}
          linkName={t("rituals.translations")}
          dashOpen={dashOpen}
          selected={splitLocation[1] === "translations"}
          headquarters={false}
          picker={false}
          admin={true}
          shop={false}
        >
          <TranslationIcon
            width={iconwidth}
            selected={splitLocation[1] === "translations"}
          />
        </DashboardSidebarListItem>
      </ul>
    </nav>
  );
  //#endregion
}
